import { Box, Button, IconButton, Toolbar } from "@mui/material";
import SearchInput from "../../tenant/search-input/search-input";
import { AppAction, AppContext } from "helpers/context";
import { useContext, useEffect, useState } from "react";
import styles from "./data-source-manage.module.scss";
import { useTranslation } from "react-i18next";
import ResourceEditModal, {
  ResourceFormType,
} from "../resource-edit-modal/resource-edit-modal";
import DeleteResourceModal from "../delete-resource-modal/delete-resource-modal";
import {
  createResource,
  updateResource,
  deleteResource,
  getResourceList,
} from "api/layer";
import {
  DataSourceType,
  DataSourceDetail,
  DataSourceListItem,
  useFileDownload,
  getFileIcon,
  getFileExtension,
  getDisplayName,
} from "components/dataSource";

// Map API response to DataSourceType
const mapApiToDataSource = (item: any): DataSourceType => {
  const attachments = item.attachments || [];
  const files = Array.isArray(attachments)
    ? attachments.map((attachment: string, index: number) => {
        const fileName = attachment.split("/").pop() || attachment;
        return {
          id: `f${index + 1}`,
          name: getDisplayName(attachment),
          size: "",
          type: getFileExtension(fileName),
          url: attachment,
        };
      })
    : undefined;

  return {
    id: String(item.resourceId),
    version: item.resourceVersion || "",
    title: item.resourceTitle || "",
    content: item.resourceContent || "",
    attachments: attachments,
    files,
    status: item.status || "draft",
  };
};

function DataSourceManage() {
  const [context, dispatch] = useContext(AppContext);
  const { t } = useTranslation();

  const [dataSources, setDataSources] = useState<DataSourceType[]>([]);
  const fetchDataSourceList = async (): Promise<
    DataSourceType[] | undefined
  > => {
    try {
      const res: any = await getResourceList({ keyword: searchKeyword });
      const details = res?.data?.releaseDetails || res?.releaseDetails || [];
      if (!Array.isArray(details)) return;
      // Filter only "Data Source" type resources
      const dataSourceDetails = details.filter(
        (item: any) => item.resourceType === "Data Source"
      );
      const mapped = dataSourceDetails.map(mapApiToDataSource);
      setDataSources(mapped);
      return mapped;
    } catch (error) {
      console.error("Failed to fetch data sources:", error);
      return undefined;
    }
  };

  const [selectedDataSource, setSelectedDataSource] =
    useState<DataSourceType | null>(null);
  const [open, setOpen] = useState(false);
  const [currentDataSource, setCurrentDataSource] =
    useState<DataSourceType | null>(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");

  const { downloadingFile, handleDownloadFile } = useFileDownload({ dispatch });

  const filteredDataSources = dataSources;

  useEffect(() => {
    fetchDataSourceList();
  }, [searchKeyword]);

  const handleSearch = (keyword: string) => {
    setSearchKeyword(keyword);
  };

  const toggleFilters = () => {
    setShowFilters((prev) => !prev);
  };

  const clearFilters = () => {
    setSearchKeyword("");
    setShowFilters(false);
  };

  const handleDataSourceClick = (dataSource: DataSourceType) => {
    setSelectedDataSource(dataSource);
  };

  const handleBackToList = () => {
    setSelectedDataSource(null);
  };

  const handleSave = async (data: ResourceFormType) => {
    const payload: any = {
      resourceType: "Data Source",
      resourceVersion: data.version,
      resourceTitle: data.title,
      resourceContent: data.content,
      attachments: data.attachments || [],
      status: data.status || "draft",
    };

    try {
      if (currentDataSource) {
        payload.resourceId = Number(data.id);
        await updateResource(payload);
      } else {
        await createResource(payload);
      }
      const updated = await fetchDataSourceList();
      if (selectedDataSource && selectedDataSource.id === data.id && updated) {
        const refreshed = updated.find((r) => r.id === data.id);
        if (refreshed) setSelectedDataSource(refreshed);
      }
    } catch (error) {
      console.error("Failed to save data source:", error);
    } finally {
      setOpen(false);
      setCurrentDataSource(null);
    }
  };

  const handleDelete = (dataSource: DataSourceType) => {
    setCurrentDataSource(dataSource);
    setOpenDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!currentDataSource) return;
    try {
      await deleteResource(Number(currentDataSource.id));
      await fetchDataSourceList();
      if (
        selectedDataSource &&
        selectedDataSource.id === currentDataSource.id
      ) {
        setSelectedDataSource(null);
      }
    } catch (error) {
      console.error("Failed to delete data source:", error);
    } finally {
      setCurrentDataSource(null);
      setOpenDeleteModal(false);
    }
  };

  if (selectedDataSource) {
    const adminActions = context.menu?.find((item) => item.id === "100002") ? (
      <div className="flex gap-2 items-center">
        <IconButton
          onClick={() => {
            setCurrentDataSource(selectedDataSource);
            setOpen(true);
          }}
          className="!text-[#00463B]"
          aria-label="edit"
        >
          <span className="material-icons">edit</span>
        </IconButton>
        <IconButton
          onClick={() => handleDelete(selectedDataSource)}
          className="!text-[#d32f2f]"
          aria-label="delete"
        >
          <span className="material-icons">delete</span>
        </IconButton>
      </div>
    ) : null;

    return (
      <>
        <DataSourceDetail
          dataSource={selectedDataSource}
          downloadingFile={downloadingFile}
          onDownloadFile={handleDownloadFile}
          attachmentsLabel={t("dataSource.attachments")}
          containerClassName={styles.container}
          actionsContent={
            <div className="mb-6 flex justify-between items-center w-full">
              <IconButton
                onClick={handleBackToList}
                className="!text-[#00463B]"
                aria-label="back"
              >
                <span className="material-icons text-[24px]">arrow_back</span>
                <span className="ml-2 text-base">{t("buttons.back")}</span>
              </IconButton>
              {adminActions}
            </div>
          }
        />

        {open && (
          <ResourceEditModal
            open={open}
            resourceType="dataSource"
            onClose={() => {
              setOpen(false);
              setCurrentDataSource(null);
            }}
            onSave={handleSave}
            data={currentDataSource || undefined}
            fileIconGetter={getFileIcon}
          />
        )}

        {openDeleteModal && currentDataSource && (
          <DeleteResourceModal
            data={currentDataSource}
            resourceType="dataSource"
            onConfirm={confirmDelete}
            onClose={() => {
              setOpenDeleteModal(false);
              setCurrentDataSource(null);
            }}
          />
        )}
      </>
    );
  }

  return (
    <div className={styles.container}>
      <Toolbar
        style={{
          justifyContent: "space-between",
          borderBottom: "1px solid #ccc",
          marginBottom: "5px",
          padding: "0",
        }}
      >
        {context.menu && context.menu.find((item) => item.id === "100002") && (
          <Button
            variant="contained"
            color="success"
            size="small"
            endIcon={<span className="material-icons">add</span>}
            onClick={() => {
              setOpen(true);
              setCurrentDataSource(null);
            }}
          >
            {t("userSystem.addDataSource")}
          </Button>
        )}
        <IconButton
          color="success"
          onClick={toggleFilters}
          style={{
            color: showFilters ? "#2e7d32" : "rgba(0, 0, 0, 0.54)",
            background: showFilters ? "#c1e5ca" : "",
          }}
        >
          <span className="material-icons">filter_list</span>
        </IconButton>
      </Toolbar>

      {showFilters && (
        <Box
          style={{
            display: "flex",
            alignItems: "center",
            padding: "8px",
            backgroundColor: "#f5f5f5",
            justifyContent: "space-between",
          }}
        >
          <Box style={{ display: "flex", flex: 1, alignItems: "center" }}>
            <SearchInput onSearch={handleSearch} />
          </Box>
          <IconButton onClick={clearFilters} aria-label="clear" color="default">
            <span className="material-icons">clear</span>
          </IconButton>
        </Box>
      )}

      <div style={{ flex: 0.95, overflow: "auto" }}>
        <div className="w-full h-auto py-4 flex flex-col gap-6">
          {filteredDataSources.map((dataSource) => (
            <DataSourceListItem
              key={dataSource.id}
              dataSource={dataSource}
              onClick={handleDataSourceClick}
              className="w-full p-4 border-y border-[#bbb] relative cursor-pointer hover:bg-gray-50 transition-colors"
            />
          ))}
        </div>
      </div>

      {open && (
        <ResourceEditModal
          open={open}
          resourceType="dataSource"
          onClose={() => {
            setOpen(false);
            setCurrentDataSource(null);
          }}
          onSave={handleSave}
          data={currentDataSource || undefined}
          fileIconGetter={getFileIcon}
        />
      )}

      {openDeleteModal && currentDataSource && (
        <DeleteResourceModal
          data={currentDataSource}
          resourceType="dataSource"
          onConfirm={confirmDelete}
          onClose={() => {
            setOpenDeleteModal(false);
            setCurrentDataSource(null);
          }}
        />
      )}
    </div>
  );
}

export default DataSourceManage;
